import { Column } from 'primereact/column'
import { DataTable } from 'primereact/datatable'
import { useState } from 'react'

import { useCQuery } from '../../../Services'
import { cap } from '../../../State'
import { translations } from '../../../translations'
import { turnTimeline } from './turnTimeline'

const language = cap.language.state || 'IT'
const t = translations[language] || translations.IT

/**
 * Guasto del provider che ha reso vuoto un esito (`PROVIDER_ERROR:<codice>` nei `guasti` di
 * `errorSummary`): il turno risulta `no_results` ma non è un risultato vero.
 */
const FAULT_CODE = /PROVIDER_ERROR:([A-Z_]+)/

const faultLabel = (codice) =>
  ({
    AIRPORT_LOOKUP_FAILED: t.agentProviderAirports,
    DINAMICO_TIMEOUT: t.agentProviderDinamico
  })[codice] || t.agentProviderUnknown

/** Esiti del turno e come si leggono a colpo d'occhio. */
const OUTCOME_VARIANT = {
  quotes_ready: 'ended',
  answer: 'ended',
  // Raccolta finita: il turno ha chiesto il via libera per il preventivo, o l'ha chiusa
  // perché su questo environment il preventivo automatico non è attivo.
  ready_to_quote: 'waiting',
  collected: 'ended',
  needs_clarification: 'waiting',
  no_results: 'waiting',
  invalid_request: 'error',
  error: 'error',
  running: 'running',
  // Stop della piattaforma: un turno chiuso bene non deve sembrare un codice sconosciuto.
  completed: 'ended',
  ACTION_SILENT: 'ended',
  TRIP_SWITCHED: 'ended',
  TRIP_SWITCH_WITHOUT_MESSAGE: 'waiting',
  CANCELED: 'waiting'
}

/**
 * Codici S4 del turno di piattaforma. La chiave è `errorCode`; l'etichetta è per chi
 * legge il backoffice, il codice resta visibile per ritrovare la riga.
 */
const ERROR_CODE_LABEL = {
  SESSION_LOAD_FAILED: () => t.agentErrorSessionLoadFailed,
  MODEL_TIMEOUT: () => t.agentErrorModelTimeout,
  MODEL_RATE_LIMITED: () => t.agentErrorModelRateLimited,
  MODEL_UNAVAILABLE: () => t.agentErrorModelUnavailable,
  MODEL_REJECTED: () => t.agentErrorModelRejected,
  MODEL_CALL_LIMIT: () => t.agentErrorModelCallLimit,
  MODEL_EMPTY_RESPONSE: () => t.agentErrorModelEmptyResponse,
  COMMIT_FAILED: () => t.agentErrorCommitFailed,
  RUN_ERROR: () => t.agentErrorRunError,
  TOOL_SERVICE_UNAVAILABLE: () => t.agentErrorToolServiceUnavailable
}

const errorCodeLabel = (codice) => ERROR_CODE_LABEL[codice]?.() || null

/** Etichetta dell'esito in tabella: i codici noti si leggono, gli altri restano come arrivano. */
const outcomeLabel = (esito) =>
  ({
    completed: t.agentOutcomeCompleted,
    ACTION_SILENT: t.agentOutcomeActionSilent,
    TRIP_SWITCHED: t.agentOutcomeTripSwitched,
    TRIP_SWITCH_WITHOUT_MESSAGE: t.agentOutcomeTripSwitchWithoutMessage,
    CANCELED: t.agentOutcomeCanceled,
    error: t.agentOutcomeError
  })[esito] ||
  errorCodeLabel(esito) ||
  esito

/**
 * Innesco del turno, dalla voce di inbox. Senza `trigger` (righe vecchie, inbox sparita)
 * la colonna resta vuota: non si inventa un messaggio.
 */
const triggerLabel = (trigger) => {
  if (!trigger?.kind) return null
  const kind =
    {
      message: t.agentTurnTriggerMessage,
      action: t.agentTurnTriggerAction,
      wakeup: t.agentTurnTriggerWakeup
    }[trigger.kind] || trigger.kind
  return trigger.tool ? `${kind} ${trigger.tool}` : kind
}

/** Tentativo della stessa voce: il primo non si numera, i successivi si leggono come una storia. */
const attemptLabel = (attempt) => {
  const n = Number(attempt)
  if (!Number.isFinite(n) || n <= 1) return null
  return language === 'EN' ? `${t.agentTurnAttempt} ${n}` : `${n}${t.agentTurnAttempt}`
}

/**
 * Colonna con intestazione visibile.
 *
 * `columnProps` della lista processi nasconde l'header e mostra l'etichetta come
 * placeholder del filtro: senza `filterDisplay="row"` — e qui filtrare per token non
 * avrebbe senso — le colonne restavano senza nome.
 */
const columnProps = (header) => ({
  header,
  style: { textAlign: 'center', border: 'none' },
  headerStyle: { textAlign: 'center' }
})

const formatDuration = (ms) => {
  if (ms == null) return '—'
  if (ms < 1000) return `${ms} ms`
  return `${(ms / 1000).toFixed(1)} s`
}

/**
 * Etichette leggibili dei provider consultati dai tool (trip_planner & co.).
 *
 * La chiave è il nome tecnico che arriva nel campo `providers` di `errorSummary`; il
 * valore è la coppia icona + etichetta che compare nella riga di dettaglio della run.
 */
/**
 * Stessi dollari per chiamata di `TARIFFA_PROVIDER_USD` nel raccoglitore del planner:
 * la card e la eval stimano lo stesso costo. Cambiare una cifra solo qui le fa divergere.
 */
const PROVIDER_LABEL = {
  google_geocoding: { label: 'Google Geocoding', icon: 'pi pi-map-marker', unitCost: 0.005 },
  google_directions: { label: 'Google Directions', icon: 'pi pi-directions', unitCost: 0.005 },
  google_routes: { label: 'Google Routes', icon: 'pi pi-map', unitCost: 0.01 },
  tavily_search: { label: 'Tavily Search', icon: 'pi pi-search', unitCost: 0.008 },
  tavily_extract: { label: 'Tavily Extract', icon: 'pi pi-file', unitCost: 0.0016 },
  local_routes: { label: 'Rotte Locali', icon: 'pi pi-database', unitCost: 0 },
  travel_core: { label: 'TravelCore', icon: 'pi pi-briefcase', unitCost: 0 },
  travelcore: { label: 'TravelCore', icon: 'pi pi-briefcase', unitCost: 0 },
  stub: { label: 'Stub', icon: 'pi pi-cog', unitCost: 0 },
  // Le fasi della ricerca di viaggio (profilo, aeroporti, luoghi, Dinamico): tempi, non costi.
  ricerca_viaggio: { label: 'Ricerca viaggio', icon: 'pi pi-stopwatch', unitCost: 0 },
  google_places: { label: 'Google Places', icon: 'pi pi-map-marker', unitCost: 0.017 },
  goelett: { label: 'Goelett', icon: 'pi pi-globe', unitCost: 0 },
  open_meteo: { label: 'Open-Meteo', icon: 'pi pi-cloud', unitCost: 0 },
  atlante_ws02: { label: 'Atlante WS02', icon: 'pi pi-id-card', unitCost: 0 },
  dinamico: { label: 'Dinamico', icon: 'pi pi-search', unitCost: 0 }
}

/**
 * Stima in dollari del costo dei provider esterni consultati durante il turno.
 *
 * `providerUsageJson` porta solo conteggi e latenze, non un costo: qui si applica il
 * tariffario indicativo di `PROVIDER_LABEL` (0 per i provider gratuiti o interni).
 * È una stima, non una fattura — usata sia nel pannello di dettaglio sia nella colonna
 * "costo provider" della tabella.
 */
const estimatedProviderCost = (usage) => {
  if (!usage?.providers?.length) return 0
  return usage.providers.reduce((sum, p) => {
    const info = PROVIDER_LABEL[p.provider]
    return sum + (info?.unitCost ?? 0) * p.calls
  }, 0)
}

/** Colore dell'esito di un tool: gli esiti d'errore si vedono a colpo d'occhio. */
const TOOL_VARIANT = {
  ...OUTCOME_VARIANT,
  unsupported: 'waiting',
  policy_found: 'ended'
}

/** JSON leggibile; un valore troppo lungo resta nel blocco scorrevole, non nella riga. */
const showJson = (valore) => {
  try {
    return JSON.stringify(valore, null, 2)
  } catch {
    return String(valore)
  }
}

/** Etichetta di una chiamata esterna o di una fase interna, nello stesso vocabolario del tariffario. */
const callLabel = (call) => {
  const info = PROVIDER_LABEL[call.provider] || {
    label: call.provider,
    icon: 'pi pi-cog',
    unitCost: 0
  }
  return { ...info, phase: call.provider === 'ricerca_viaggio' }
}

/** Costo stimato di una singola chiamata. Zero se il provider non è a pagamento o è in cache. */
const callCost = (call) => {
  if (call.cached) return 0
  return PROVIDER_LABEL[call.provider]?.unitCost ?? 0
}

/** Nome del servizio da un metodo di poll: `GetRailOffers` → `treno`. */
const SERVICE_LABEL = {
  Air: 'aereo',
  Rail: 'treno',
  Hotel: 'hotel',
  Car: 'auto',
  City: 'città',
  Airport: 'aeroporto',
  Railway: 'stazione'
}

/** Quante offerte dice l'ultima lettura, e se la ricerca si è chiusa. */
const groupOutcome = (calls) => {
  const last = [...calls].reverse().find((call) => call.outputSummary) ?? calls[calls.length - 1]
  const match = /(\d+)\s+(offerte|soluzioni|punti)/.exec(last?.outputSummary ?? '')
  const closed = /ricerca chiusa/.test(last?.outputSummary ?? '')
  const open = /ancora in corso/.test(last?.outputSummary ?? '')
  return {
    count: match ? Number(match[1]) : null,
    unit: match?.[2] ?? null,
    closed,
    open
  }
}

const clockLabel = (ms) => {
  if (ms == null) return ''
  const data = new Date(ms)
  if (Number.isNaN(data.getTime())) return ''
  return data.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit', second: '2-digit' })
}

/** Un punto sulla linea, con l'orario a sinistra. L'ultimo non prosegue il tratto. */
const TimelinePoint = ({ at, ultimo, errore, children }) => (
  <div style={{ display: 'flex', gap: 10 }}>
    <div
      style={{
        width: 58,
        flexShrink: 0,
        fontFamily: 'var(--font-mono)',
        fontSize: 10,
        opacity: 0.55,
        paddingTop: 2,
        textAlign: 'right'
      }}
    >
      {clockLabel(at)}
    </div>
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: 12 }}>
      <span
        style={{
          width: 8,
          height: 8,
          borderRadius: '50%',
          background: errore ? 'var(--red-500)' : 'var(--primary-color, var(--blue-400))',
          flexShrink: 0,
          marginTop: 4
        }}
      />
      {ultimo ? null : (
        <span style={{ width: 1, flex: 1, background: 'var(--surface-border)', minHeight: 12 }} />
      )}
    </div>
    <div style={{ flex: 1, minWidth: 0, paddingBottom: 10 }}>{children}</div>
  </div>
)

/**
 * Una voce della sequenza: tool del modello, oppure chiamata esterna o fase interna.
 * Le chiamate sono indentate: sono lavoro del codice, non una decisione del modello.
 */
const SequenceItem = ({ voce, ultimo }) => {
  const blocco = {
    margin: '4px 0 0',
    padding: 8,
    maxHeight: 260,
    overflow: 'auto',
    background: 'var(--surface-ground, rgba(127,127,127,0.08))',
    borderRadius: 6,
    fontFamily: 'var(--font-mono)',
    fontSize: 11,
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word'
  }
  if (voce.kind === 'user') {
    return (
      <TimelinePoint at={voce.at} ultimo={ultimo}>
        <div className="ntable__meta">
          <span style={{ color: 'var(--blue-400)' }}>{t.agentTraceUser}:</span> {voce.text}
        </div>
      </TimelinePoint>
    )
  }
  if (voce.kind === 'reply') {
    return (
      <TimelinePoint at={voce.at} ultimo={ultimo}>
        <div className="ntable__meta">
          <span style={{ color: 'var(--green-400)' }}>{t.agentTraceReply}:</span> {voce.text}
        </div>
      </TimelinePoint>
    )
  }
  if (voce.kind === 'harness') {
    const hasDetail = voce.step.input || voce.step.output
    const label = (
      <>
        <i className="pi pi-cog" style={{ fontSize: 11, marginRight: 4, opacity: 0.6 }} />
        <span style={{ fontFamily: 'var(--font-mono)', opacity: 0.7 }}>{voce.step.phase}</span>
        <span> · {voce.step.label}</span>
        {voce.step.durationMs != null ? (
          <span style={{ opacity: 0.6 }}> · {formatDuration(voce.step.durationMs)}</span>
        ) : null}
      </>
    )
    return (
      <TimelinePoint at={voce.at} ultimo={ultimo}>
        {hasDetail ? (
          <details className="agent-trace-details">
            <summary>
              <i className="pi pi-angle-right agent-trace-details__chevron" aria-hidden="true" />
              {label}
            </summary>
            {voce.step.input ? (
              <>
                <div style={{ color: 'var(--blue-400)', marginTop: 4 }}>{t.agentTraceInput}</div>
                <pre style={blocco}>{voce.step.input}</pre>
              </>
            ) : null}
            {voce.step.output ? (
              <>
                <div style={{ color: 'var(--green-400)', marginTop: 6 }}>{t.agentTraceOutput}</div>
                <pre style={blocco}>{voce.step.output}</pre>
              </>
            ) : null}
          </details>
        ) : (
          <div className="ntable__meta">{label}</div>
        )}
      </TimelinePoint>
    )
  }
  if (voce.kind === 'group') {
    const first = voce.calls[0]
    const info = callLabel(first)
    const method = first.method ?? ''
    const service = method.match(/^Get(\w+?)(?:Offers|LocationPoints)$/)?.[1]
    const city = /LocationPoints$/.test(method)
      ? voce.calls.map((call) => /^([^,]+),/.exec(call.outputSummary ?? '')?.[1]).find(Boolean)
      : null
    const name = city || SERVICE_LABEL[service] || info.label
    const points = /LocationPoints$/.test(method)
    const unit = points || /Offers$/.test(method) ? t.agentTraceReads : t.agentTraceAttempts
    const outcome = groupOutcome(voce.calls)
    const outcomeUnit =
      outcome.unit === 'offerte'
        ? t.agentTraceOffers
        : outcome.unit === 'soluzioni'
          ? t.agentTraceSolutions
          : outcome.unit
    const ms = voce.calls.reduce((sum, call) => sum + (call.durationMs || 0), 0)
    const cost = voce.calls.reduce((sum, call) => sum + callCost(call), 0)
    const errori = voce.calls.filter((call) => call.ok === false).length
    const dove = first.inputSummary ? ` · ${first.inputSummary}` : ''
    return (
      <TimelinePoint at={voce.at} ultimo={ultimo} errore={errori === voce.calls.length}>
        <details className="agent-trace-details" style={{ marginLeft: 8 }}>
          <summary>
            <i className="pi pi-angle-right agent-trace-details__chevron" aria-hidden="true" />
            <i className={info.icon} style={{ fontSize: 11, marginRight: 4 }} />
            <span style={{ fontWeight: 500 }}>{name}</span>
            <span style={{ opacity: 0.7 }}>
              {' '}
              · {voce.calls.length} {unit}
              {dove}
            </span>
            <span style={{ opacity: 0.6 }}> · {formatDuration(ms)}</span>
            {outcome.count != null ? (
              <span style={{ opacity: 0.7 }}>
                {' '}
                · {outcome.count} {outcomeUnit}
              </span>
            ) : null}
            {outcome.closed ? <span style={{ opacity: 0.7 }}> · {t.agentTraceClosed}</span> : null}
            {outcome.open ? (
              <span style={{ color: 'var(--orange-500)' }}> · {t.agentTraceOpen}</span>
            ) : null}
            {cost > 0 ? (
              <span style={{ fontFamily: 'var(--font-mono)', opacity: 0.7 }}>
                {' '}
                · ~${cost.toFixed(4)}
              </span>
            ) : null}
            {errori ? <span style={{ color: 'var(--red-500)' }}> · {errori} errori</span> : null}
          </summary>
          <div style={{ marginLeft: 12 }}>
            {voce.calls.map((call, i) => (
              <div key={i} className="ntable__meta" style={{ marginTop: 4 }}>
                <span style={{ fontFamily: 'var(--font-mono)' }}>
                  {clockLabel(call.startedAt) || `#${i + 1}`}
                </span>
                <span style={{ opacity: 0.6 }}> · {formatDuration(call.durationMs)}</span>
                {call.outputSummary ? <span> · {call.outputSummary}</span> : null}
                {call.ok === false ? (
                  <span style={{ color: 'var(--red-500)' }}> · errore</span>
                ) : null}
              </div>
            ))}
          </div>
        </details>
      </TimelinePoint>
    )
  }
  if (voce.kind === 'call') {
    const info = callLabel(voce.call)
    const cost = callCost(voce.call)
    const hasDetail = voce.call.inputSummary || voce.call.outputSummary || voce.call.error
    const label = (
      <>
        <i className={info.icon} style={{ fontSize: 11, marginRight: 4 }} />
        <span style={{ fontWeight: 500 }}>{info.label}</span>
        <span style={{ fontFamily: 'var(--font-mono)', opacity: 0.7 }}> .{voce.call.method}</span>
        <span style={{ opacity: 0.55 }}>
          {' '}
          · {info.phase ? t.agentTracePhase : t.agentTraceExternal}
        </span>
        <span style={{ opacity: 0.6 }}> · {formatDuration(voce.call.durationMs)}</span>
        {cost > 0 ? (
          <span style={{ fontFamily: 'var(--font-mono)', opacity: 0.7 }}>
            {' '}
            · ~${cost.toFixed(4)}
          </span>
        ) : null}
        {voce.call.cached ? <span style={{ opacity: 0.55 }}> · cache</span> : null}
        {voce.call.ok === false ? <span style={{ color: 'var(--red-500)' }}> · errore</span> : null}
      </>
    )
    return (
      <TimelinePoint at={voce.at} ultimo={ultimo} errore={voce.call.ok === false}>
        {hasDetail ? (
          <details className="agent-trace-details" style={{ marginLeft: 8 }}>
            <summary>
              <i className="pi pi-angle-right agent-trace-details__chevron" aria-hidden="true" />
              {label}
            </summary>
            {voce.call.inputSummary ? (
              <>
                <div style={{ color: 'var(--blue-400)', marginTop: 4 }}>{t.agentTraceInput}</div>
                <pre style={blocco}>{voce.call.inputSummary}</pre>
              </>
            ) : null}
            {voce.call.outputSummary ? (
              <>
                <div style={{ color: 'var(--green-400)', marginTop: 6 }}>{t.agentTraceOutput}</div>
                <pre style={blocco}>{voce.call.outputSummary}</pre>
              </>
            ) : null}
            {voce.call.error ? (
              <div style={{ color: 'var(--red-500)', marginTop: 6 }}>{voce.call.error}</div>
            ) : null}
          </details>
        ) : (
          <div className="ntable__meta" style={{ marginLeft: 8 }}>
            {label}
          </div>
        )}
      </TimelinePoint>
    )
  }
  const call = voce.call
  const inErrore = call.outcome === 'error'
  const badge = (testo, title) => (
    <span
      className="nora-status nora-status--neutral"
      style={{ fontSize: 10, padding: '1px 6px', marginLeft: 4 }}
      title={title}
    >
      <span className="nora-status__label">{testo}</span>
    </span>
  )
  return (
    <TimelinePoint at={voce.at} ultimo={ultimo} errore={inErrore}>
      <details className="agent-trace-details">
        <summary>
          <i className="pi pi-angle-right agent-trace-details__chevron" aria-hidden="true" />
          <i className="pi pi-wrench" style={{ fontSize: 11, marginRight: 4 }} />
          <span style={{ fontFamily: 'var(--font-mono)' }}>{call.name}</span>{' '}
          <span className={`nora-status nora-status--${TOOL_VARIANT[call.outcome] || 'neutral'}`}>
            <span className="nora-status__dot" />
            <span className="nora-status__label">{call.outcome || '—'}</span>
          </span>
          {call.service ? badge(call.service, t.agentTraceService) : null}
          {call.origin ? badge(call.origin, t.agentTraceOrigin) : null}
          {call.durationMs != null ? (
            <span style={{ opacity: 0.6 }}> · {formatDuration(call.durationMs)}</span>
          ) : null}
          {inErrore && call.errorCode ? (
            <span style={{ color: 'var(--red-500)' }}> · {call.errorCode}</span>
          ) : null}
          {inErrore && call.retryable != null ? (
            <span style={{ opacity: 0.7 }}>
              {' '}
              · {call.retryable ? t.agentTraceRetryable : t.agentTraceNotRetryable}
            </span>
          ) : null}
        </summary>
        <div style={{ marginLeft: 12 }}>
          {/* Le tracce salvate prima del registro completo hanno solo nome, esito e durata:
              mostrare `null` come input e output farebbe credere a un tool chiamato a vuoto. */}
          {call.args == null && call.data == null && !call.truncated ? (
            <div style={{ marginTop: 4, opacity: 0.7 }}>{t.agentTraceNotRecorded}</div>
          ) : (
            <>
              <div style={{ color: 'var(--blue-400)', marginTop: 4 }}>{t.agentTraceInput}</div>
              <pre style={blocco}>{showJson(call.args)}</pre>
              <div style={{ color: 'var(--green-400)', marginTop: 6 }}>{t.agentTraceOutput}</div>
              {call.truncated ? (
                <>
                  <div style={{ color: 'var(--orange-500)' }}>
                    {t.agentTraceTruncated}
                    {call.truncatedBytes ? ` (${call.truncatedBytes} byte)` : ''}
                  </div>
                  {call.summary ? (
                    <>
                      <div style={{ marginTop: 4 }}>{t.agentTraceSummary}</div>
                      <pre style={blocco}>{showJson(call.summary)}</pre>
                    </>
                  ) : null}
                </>
              ) : (
                <pre style={blocco}>{showJson(call.data)}</pre>
              )}
            </>
          )}
          {Array.isArray(call.widgets) && call.widgets.length ? (
            <div style={{ marginTop: 6 }}>
              {t.agentTraceWidgets}: {call.widgets.join(', ')}
            </div>
          ) : null}
          {call.providerCalls ? (
            <div style={{ marginTop: 4 }}>
              {t.agentTraceProviderCalls}: {call.providerCalls}
            </div>
          ) : null}
        </div>
      </details>
    </TimelinePoint>
  )
}

/**
 * La traccia completa di un turno, in un solo elenco: messaggio, tool del modello,
 * chiamate esterne e fasi interne, risposta. Viene dai messaggi del turno e da
 * `providerUsageJson`.
 *
 * Un output oltre il tetto di byte dell'Executor è dichiarato troncato: «campo vuoto» e
 * «troppo grande per essere salvato» non sono la stessa cosa.
 */
const TurnTrace = ({ trace, usage }) => {
  if (!trace) return null
  // Il link alla trace Langfuse c'è solo se il deployment configura la base URL e il turno ha un
  // `traceId`: l'API non manda `langfuseUrl` in nessun altro caso.
  const link = trace.langfuseUrl ? (
    <a href={trace.langfuseUrl} target="_blank" rel="noopener noreferrer" className="ntable__meta">
      <i className="pi pi-external-link" style={{ fontSize: 11, marginRight: 4 }} />
      {t.agentTraceLangfuse}
    </a>
  ) : null
  if (!trace.available) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        <div className="ntable__meta" style={{ opacity: 0.7 }}>
          {t.agentTraceNone}
        </div>
        {link}
      </div>
    )
  }
  const sequence = turnTimeline(trace, usage)
  const hasTimes = sequence.some((voce) => voce.at != null)
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <div className="ntable__meta" style={{ fontWeight: 600 }}>
        {t.agentTrace}
      </div>
      {link}
      {/* Sulla piattaforma i tool stanno sempre nel turno: l'avviso vale solo se mancano i messaggi. */}
      {trace.source === 'turn' && !trace.texts?.length && !trace.userMessage ? (
        <div className="ntable__meta" style={{ opacity: 0.7 }}>
          {t.agentTraceFromTurn}
        </div>
      ) : null}
      {!hasTimes && (usage?.rawCalls?.length || trace.toolCalls?.length) ? (
        <div className="ntable__meta" style={{ opacity: 0.55 }}>
          {t.agentTraceOrderSaved}
        </div>
      ) : null}
      {usage?.omittedCalls ? (
        <div className="ntable__meta" style={{ color: 'var(--orange-500)' }}>
          {usage.omittedCalls} {t.agentTraceOmitted}
        </div>
      ) : null}
      {sequence.length ? (
        sequence.map((voce, i) => (
          <SequenceItem key={i} voce={voce} ultimo={i === sequence.length - 1} />
        ))
      ) : (
        <div className="ntable__meta" style={{ opacity: 0.7 }}>
          {t.agentTraceEmpty}
        </div>
      )}
    </div>
  )
}

/**
 * Gli esiti dei tool del turno, salvati in sequenza.
 *
 * È il "cosa ha fatto" della run: ogni voce è la risposta compatta che una capability ha
 * dato al modello. Si legge come una traccia, non come un log tecnico.
 *
 * Dalla versione con `tracciaCompatta`, ogni voce porta anche `tool` (nome dello
 * strumento), `ms` (durata in millisecondi) e `providers` (sorgenti dati consultate).
 */
const RunDetail = ({ turno }) => {
  // Un errore di piattaforma è già `<stage>: <messaggio>`: non è la lista di esiti
  // compatti dell'Executor, e spezzarlo sul «:» lo farebbe leggere come un passo.
  const errorePiattaforma = turno.status === 'error' && (turno.errorCode || turno.errorSummary)
  const esiti = errorePiattaforma
    ? []
    : String(turno.errorSummary || '')
        .split(' | ')
        .map((e) => e.trim())
        .filter(Boolean)

  const readOutcome = (grezzo) => {
    const guasto = FAULT_CODE.exec(grezzo)?.[1]
    if (guasto && !grezzo.trim().startsWith('{')) {
      return { testo: faultLabel(guasto), json: null, guasto: true }
    }
    try {
      const j = JSON.parse(grezzo)
      const parti = [j.tool || j.outcome]
      if (j.tool && j.outcome) parti.push(j.outcome)
      if (j.code) parti.push(j.code)
      if (j.reasonCode) parti.push(j.reasonCode)
      if (j.count) parti.push(`${j.count} soluzioni`)
      if (j.alternatives != null) parti.push(`${j.alternatives} alternative`)
      if (j.services?.length) parti.push(j.services.join(', '))
      if (j.missing?.length)
        parti.push(
          `mancano: ${j.missing.map((m) => (typeof m === 'string' ? m : m.campo)).join(', ')}`
        )
      if (j.revision != null) parti.push(`rev ${j.revision}`)
      if (j.limitations?.length) parti.push(`${t.agentLimitations}: ${j.limitations.join('; ')}`)
      if (j.ms != null) parti.push(formatDuration(j.ms))
      return { testo: parti.filter(Boolean).join(' · '), json: j }
    } catch {
      return { testo: grezzo, json: null }
    }
  }

  // La piattaforma non ha thread né versione: l'id visibile al viaggiatore è il prefisso
  // del turno, lo stesso `diagnosticId`. Le righe dell'Executor tengono l'intestazione vecchia.
  const intestazione =
    turno.threadId || turno.agentVersion
      ? `${turno.threadId?.slice(0, 8) || '—'} · ${turno.agentVersion || '—'}`
      : `${t.agentTurnId} ${String(turno.id || '').slice(0, 8) || '—'}`
  const etichettaErrore = errorCodeLabel(turno.errorCode)

  return (
    <div style={{ padding: '10px 16px', display: 'flex', flexDirection: 'column', gap: 8 }}>
      <div className="ntable__meta">
        {t.agentTurnDetail} · {intestazione}
      </div>
      {errorePiattaforma ? (
        <div className="ntable__meta" style={{ color: 'var(--red-500)' }}>
          {[etichettaErrore, turno.errorCode, turno.errorSummary].filter(Boolean).join(' · ')}
        </div>
      ) : null}
      {esiti.length ? (
        <ol
          style={{ margin: 0, paddingLeft: 18, display: 'flex', flexDirection: 'column', gap: 6 }}
        >
          {esiti.map((grezzo, i) => {
            const { testo, json, guasto } = readOutcome(grezzo)
            const providers = json?.providers ?? []
            return (
              <li
                key={i}
                className="ntable__meta"
                title={json ? JSON.stringify(json, null, 2) : ''}
              >
                {guasto ? (
                  <span className="nora-status nora-status--error" style={{ marginRight: 6 }}>
                    <span className="nora-status__dot" />
                    <span className="nora-status__label">{t.agentProviderError}</span>
                  </span>
                ) : null}
                <span>{testo}</span>
                {providers.length > 0 && (
                  <div
                    style={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: 4,
                      marginTop: 3
                    }}
                  >
                    {providers.map((prov) => {
                      const info = PROVIDER_LABEL[prov] || { label: prov, icon: 'pi pi-cog' }
                      return (
                        <span
                          key={prov}
                          className="nora-status nora-status--neutral"
                          style={{ fontSize: 10, padding: '1px 6px' }}
                          title={prov}
                        >
                          <i className={info.icon} style={{ fontSize: 10, marginRight: 3 }} />
                          <span>{info.label}</span>
                        </span>
                      )
                    })}
                  </div>
                )}
              </li>
            )
          })}
        </ol>
      ) : turno.engine === 'platform' ? null : (
        // Gli esiti compatti erano dell'Executor; la piattaforma li mostra nella traccia.
        <span className="ntable__meta">{t.agentNoSteps}</span>
      )}
      <div className="ntable__meta" style={{ fontFamily: 'var(--font-mono)' }}>
        {t.agentTokens}: {turno.tokenIn} ↓ / {turno.tokenOut} ↑ · {t.agentCost}:{' '}
        {formatCost(turno.costJson)} · {t.agentDuration}: {formatDuration(turno.durationMs)}
      </div>
      <TurnTrace trace={turno.trace} usage={turno.providerUsageJson} />
    </div>
  )
}

const formatCost = (costJson) => {
  if (!costJson || typeof costJson.totale !== 'number') return '—'
  const valuta = costJson.valuta === 'USD' ? '$' : '€'
  // I costi di un turno sono frazioni di centesimo: quattro decimali o si legge "0,00".
  return `${valuta} ${costJson.totale.toFixed(4)}`
}

/**
 * Il costo provider è sempre una stima in dollari (`unitCost` di `PROVIDER_LABEL`),
 * indipendentemente dalla valuta del costo LLM: la colonna "costo totale" li somma
 * comunque, dato che oggi il tenant è sempre fatturato in un'unica valuta.
 */
const formatProviderCost = (usage) => {
  const stima = estimatedProviderCost(usage)
  if (!stima) return '—'
  return `$ ${stima.toFixed(4)}`
}

const formatTotalCost = (costJson, usage) => {
  const llm = typeof costJson?.totale === 'number' ? costJson.totale : 0
  const provider = estimatedProviderCost(usage)
  if (!llm && !provider) return '—'
  const valuta = costJson?.valuta === 'USD' ? '$' : '€'
  return `${valuta} ${(llm + provider).toFixed(4)}`
}

/**
 * La controparte agentica della lista processi.
 *
 * Un turno dell'agente è l'equivalente di un `process` del motore legacy: un messaggio
 * accettato, una esecuzione, un esito. Qui si vede cosa ha fatto — quante volte ha
 * interrogato il modello, quanti tool ha usato, quanto è costato e perché si è fermato.
 *
 * La scheda non compare per le conversazioni legacy: se non ci sono turni, non c'è
 * niente da mostrare e non serve una riga vuota in più nella pagina.
 */
export function AgentTurnsCard({ conversationId }) {
  const { data, isSuccess } = useCQuery(['agentTurns', conversationId])
  const [espansi, setEspansi] = useState([])
  const turni = data?.turns || []

  if (!isSuccess) return null
  if (!turni.length && !data?.unavailable) return null

  const righe = turni.map((turno) => ({
    ...turno,
    // Un turno in errore si legge dal codice, anche se porta uno stopReason di chiusura.
    esito:
      turno.status === 'error'
        ? turno.errorCode || turno.status
        : turno.stopReason || turno.errorCode || turno.status,
    // `errorSummary` porta l'esito compatto restituito dalla capability: è la sola
    // spiegazione di perché il turno è finito così, e va letta senza aprire i log.
    dettaglio: turno.errorSummary || '',
    // Il guasto dichiarato dal turno, o una chiamata a un provider finita male: anche senza un
    // codice il turno non è «pulito».
    guastoProvider:
      FAULT_CODE.exec(turno.errorSummary || '')?.[1] ||
      ((turno.providerUsageJson?.providers ?? []).some((p) => p.errors > 0)
        ? 'PROVIDER_CALL_FAILED'
        : null)
  }))

  const inCoda = Object.entries(data?.queue || {})
    .map(([stato, n]) => `${stato}: ${n}`)
    .join(' · ')

  return (
    <div className="ncard" style={{ padding: 0, marginTop: 16 }}>
      <div className="ncard__head">
        <div className="ncard__ico">
          <i className="pi pi-sparkles" style={{ fontSize: 16 }} />
        </div>
        <div>
          <h3 className="ncard__title">{t.agentTurnsTitle}</h3>
          {inCoda ? (
            <span className="ntable__meta">
              {t.agentQueueState}: {inCoda}
            </span>
          ) : null}
          {/*
            Le due metriche che dicono se il motore nuovo è **meglio** del vecchio, non
            quanto costa: quanti turni per arrivare al preventivo, e quanto ha aspettato il
            viaggiatore prima di vedere comparire qualcosa. Le calcola l'Executor, che è il
            solo a conoscere la sequenza dei turni.
          */}
          {data?.metrics?.turnsToQuote || data?.metrics?.firstTokenMsMedian ? (
            <span className="ntable__meta">
              {data.metrics.turnsToQuote
                ? `${t.agentTurnsToQuote}: ${data.metrics.turnsToQuote}`
                : `${t.agentTurnsToQuote}: —`}
              {data.metrics.firstTokenMsMedian
                ? ` · ${t.agentFirstToken}: ${(data.metrics.firstTokenMsMedian / 1000).toFixed(1)}s`
                : ''}
            </span>
          ) : null}
        </div>
      </div>

      {data?.unavailable ? (
        <div style={{ padding: 16 }} className="ntable__meta">
          {t.agentTurnsUnavailable}
        </div>
      ) : (
        <DataTable
          style={{ width: '100%' }}
          className="TableSignatures ntable-datatable"
          value={righe}
          emptyMessage={t.agentTurnsEmpty}
          responsiveLayout="scroll"
          sortField="sequence"
          sortOrder={-1}
          rows={10}
          paginator={righe.length > 10}
          dataKey="id"
          expandedRows={espansi}
          onRowToggle={(e) => setEspansi(e.data)}
          rowExpansionTemplate={(turno) => <RunDetail turno={turno} />}
        >
          <Column expander style={{ width: 44, border: 'none' }} />
          <Column
            field="sequence"
            {...columnProps(t.agentTurn)}
            body={({ sequence }) => <span className="ntable__num">#{sequence}</span>}
          />
          <Column
            field="trigger"
            {...columnProps(t.agentTurnTrigger)}
            body={({ trigger, attempt }) => {
              const testo = triggerLabel(trigger)
              const tentativo = attemptLabel(attempt)
              if (!testo && !tentativo) return <span className="ntable__meta">—</span>
              return (
                <span className="ntable__meta">
                  {testo || '—'}
                  {tentativo ? <span style={{ opacity: 0.7 }}> · {tentativo}</span> : null}
                </span>
              )
            }}
          />
          <Column
            field="intent"
            {...columnProps(t.agentIntent)}
            body={({ intent }) =>
              intent ? (
                <span className="ntable__meta">{intent}</span>
              ) : (
                <span className="ntable__meta">—</span>
              )
            }
          />
          <Column
            field="firstTokenMs"
            {...columnProps(t.agentFirstToken)}
            body={({ firstTokenMs }) => (
              <span className="ntable__num">
                {Number.isFinite(Number(firstTokenMs)) && Number(firstTokenMs) > 0
                  ? `${(Number(firstTokenMs) / 1000).toFixed(1)}s`
                  : '—'}
              </span>
            )}
          />
          <Column
            field="esito"
            {...columnProps(t.agentOutcome)}
            body={({ esito, status, dettaglio, guastoProvider }) => (
              <span style={{ display: 'inline-flex', flexDirection: 'column', gap: 3 }}>
                <span
                  className={`nora-status nora-status--${status === 'error' ? 'error' : OUTCOME_VARIANT[esito] || 'neutral'}`}
                  title={dettaglio}
                >
                  <span className="nora-status__dot" />
                  <span className="nora-status__label">{outcomeLabel(esito)}</span>
                </span>
                {guastoProvider ? (
                  <span
                    className="nora-status nora-status--error"
                    title={faultLabel(guastoProvider)}
                  >
                    <span className="nora-status__dot" />
                    <span className="nora-status__label">{t.agentProviderError}</span>
                  </span>
                ) : null}
              </span>
            )}
          />
          <Column
            field="model"
            {...columnProps(t.agentModel)}
            body={({ model }) => <span className="ntable__meta">{model || '—'}</span>}
          />
          <Column
            {...columnProps(t.agentCalls)}
            body={({ nModelCalls, nToolCalls }) => (
              <span className="ntable__meta" style={{ fontFamily: 'var(--font-mono)' }}>
                {nModelCalls} modello · {nToolCalls} tool
              </span>
            )}
          />
          <Column
            {...columnProps(t.agentTokens)}
            body={({ tokenIn, tokenOut }) => (
              <span className="ntable__meta" style={{ fontFamily: 'var(--font-mono)' }}>
                {tokenIn} ↓ / {tokenOut} ↑
              </span>
            )}
          />
          <Column
            {...columnProps(t.agentCost)}
            body={({ costJson }) => (
              <span className="ntable__meta" style={{ fontFamily: 'var(--font-mono)' }}>
                {formatCost(costJson)}
              </span>
            )}
          />
          <Column
            {...columnProps(t.agentProviderCost)}
            body={({ providerUsageJson }) => (
              <span
                className="ntable__meta"
                style={{ fontFamily: 'var(--font-mono)' }}
                title={
                  providerUsageJson?.providers
                    ?.map((p) => `${p.provider}: ${p.calls}×`)
                    .join(', ') || ''
                }
              >
                {formatProviderCost(providerUsageJson)}
              </span>
            )}
          />
          <Column
            {...columnProps(t.agentTotalCost)}
            body={({ costJson, providerUsageJson }) => (
              <span
                className="ntable__meta"
                style={{ fontFamily: 'var(--font-mono)', fontWeight: 600 }}
              >
                {formatTotalCost(costJson, providerUsageJson)}
              </span>
            )}
          />
          <Column
            field="durationMs"
            {...columnProps(t.agentDuration)}
            body={({ durationMs }) => (
              <span className="ntable__meta" style={{ fontFamily: 'var(--font-mono)' }}>
                {formatDuration(durationMs)}
              </span>
            )}
          />
          <Column
            {...columnProps('API')}
            body={({ providerUsageJson }) =>
              providerUsageJson?.totalCalls ? (
                <span
                  className="ntable__meta"
                  style={{ fontFamily: 'var(--font-mono)' }}
                  title={
                    providerUsageJson.providers
                      ?.map((p) => `${p.provider}: ${p.calls}×`)
                      .join(', ') || ''
                  }
                >
                  {providerUsageJson.totalCalls} call
                </span>
              ) : (
                <span className="ntable__meta">—</span>
              )
            }
          />
        </DataTable>
      )}
    </div>
  )
}
